import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Trophy, Award, Star, Zap, Shield, Target, 
  Medal, Sparkles, Flame, ChevronDown, Filter, Search,
  Calendar, Users, TrendingUp, BarChart3, Eye, Activity,
  Swords, Clock, MapPin
} from 'lucide-react';

// Optimized Opponent Avatar Component with memoization
const OpponentAvatar = React.memo(({ 
  opponentName, 
  className = "", 
  fallbackIcon: FallbackIcon = Users,
  size = "small"
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const imageSrc = `/${opponentName.toLowerCase()}angry.png`;
  
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);
  
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);
  
  // Memoized size mappings
  const sizeConfig = useMemo(() => ({
    small: { container: "w-10 h-10", icon: "h-5 w-5" },
    medium: { container: "w-16 h-16", icon: "h-8 w-8" },
    large: { container: "w-20 h-20", icon: "h-10 w-10" }
  }[size]), [size]);
  
  if (imageError || !imageSrc) {
    return (
      <div className={`bg-red-500/30 rounded-full flex items-center justify-center border-3 border-red-400/50 shadow-lg ${sizeConfig.container} ${className}`}>
        <FallbackIcon className={`${sizeConfig.icon} text-red-400`} />
      </div>
    );
  }
  
  return (
    <div className={`relative rounded-full overflow-hidden border-3 border-red-400/60 shadow-xl ${sizeConfig.container} ${className}`}>
      <img
        src={imageSrc}
        alt={`${opponentName} angry avatar`}
        className={`w-full h-full object-cover transition-all duration-300 hover:scale-110 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onError={handleImageError}
        onLoad={handleImageLoad}
        loading="lazy"
      />
      {!imageLoaded && (
        <div className="absolute inset-0 bg-red-500/20 rounded-full flex items-center justify-center">
          <FallbackIcon className={`${sizeConfig.icon} text-red-400`} />
        </div>
      )}
      <div className="absolute inset-0 rounded-full ring-2 ring-red-400/30 ring-offset-1 ring-offset-transparent"></div>
    </div>
  );
});

// Optimized Custom Crown component with modal functionality
const CustomCrown = React.memo(({ className = "h-20 w-20", clickable = false, onOpenModal }) => {
  const handleCrownClick = useCallback(() => {
    if (clickable && onOpenModal) {
      onOpenModal("/thebeastcrown.png", "The Beast Crown");
    }
  }, [clickable, onOpenModal]);

  const crownElement = (
    <img 
      src="/thebeastcrown.png" 
      alt="Beast Crown" 
      className={`${className} drop-shadow-lg ${clickable ? 'cursor-pointer hover:scale-105 transition-transform duration-300' : ''}`}
      style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
      loading="lazy"
    />
  );

  if (clickable) {
    return (
      <button
        onClick={handleCrownClick}
        className="focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded-lg p-1"
        aria-label="View Beast Crown"
      >
        {crownElement}
      </button>
    );
  }

  return crownElement;
});

const HallOfChampions = () => {
  const [isHeaderAnimated, setIsHeaderAnimated] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Memoized championship data to prevent unnecessary re-renders
  const championshipData = useMemo(() => [
    {
      year: 2025,
      champion: "Oster",
      teamName: "IDrinkAndTradeThings",
      opponent: "Shuds",
      score: "123.30 - 99.74",
      margin: 23.56,
      mvp: "Derrick Henry",
      mvpStats: "45.60 pts (proj 13.17)",
      isRecent: true,
      championshipNumber: 1
    },
    {
      year: 2024,
      champion: "Kevin",
      teamName: "Cee-3Deebo",
      opponent: "Jake",
      score: "146.58 - 86.68",
      margin: 59.90,
      mvp: "Tee Higgins",
      mvpStats: "29.10 pts (proj 9.76)",
      isRecent: false,
      championshipNumber: 3
    },
    {
      year: 2023,
      champion: "Lyons",
      teamName: "Hodors Hodors",
      opponent: "Nipples",
      score: "108.48 - 86.66",
      margin: 21.82,
      mvp: "Kyren Williams",
      mvpStats: "28.10 pts (proj 13.73)",
      isRecent: true,
      championshipNumber: 2
    },
    {
      year: 2022,
      champion: "Shuds",
      teamName: "LaMarvelous",
      opponent: "Nipples",
      score: "97.42 - 60.90",
      margin: 36.52,
      mvp: "Dalton Schultz",
      mvpStats: "17.60 pts (proj 5.94)",
      isRecent: true,
      championshipNumber: 2
    },
    {
      year: 2021,
      champion: "Kevin",
      teamName: "Tom Bombradyl",
      opponent: "Nipples",
      score: "120.30 - 102.90",
      margin: 17.40,
      mvp: "Tom Brady",
      mvpStats: "27.40 pts (proj 24.21)",
      championshipNumber: 2
    },
    {
      year: 2020,
      champion: "Raffy",
      teamName: "NOTIMPRESSED",
      opponent: "Nipples",
      score: "126.23 - 112.30",
      margin: 13.93,
      mvp: "Mike Evans",
      mvpStats: "30.10 pts (proj 10.04)",
      championshipNumber: 1
    },
    {
      year: 2019,
      champion: "Nipples",
      teamName: "Chark Week",
      opponent: "Lyons",
      score: "86.76 - 78.56",
      margin: 8.20,
      mvp: "Kenyan Drake",
      mvpStats: "30.40 pts (proj 11.85)",
      championshipNumber: 3
    },
    {
      year: 2018,
      champion: "Nipples",
      teamName: "Cobra Kai",
      opponent: "Lyons",
      score: "111.56 - 104.76",
      margin: 6.80,
      mvp: "Zach Ertz",
      mvpStats: "23 pts (proj 9.58)",
      championshipNumber: 2
    },
    {
      year: 2017,
      champion: "Kevin",
      teamName: "A Gurley Has No Name",
      opponent: "Nipples",
      score: "119.72 - 105.28",
      margin: 14.44,
      mvp: "Todd Gurley II",
      mvpStats: "39.60 pts (proj 17.19)",
      championshipNumber: 1
    },
    {
      year: 2016,
      champion: "Dan",
      teamName: "Suggs Thugs",
      opponent: "Kevin",
      score: "98.66 - 91.88",
      margin: 6.78,
      mvp: "Travis Kelce",
      mvpStats: "22 pts (proj 7.09)",
      championshipNumber: 1
    },
    {
      year: 2015,
      champion: "Joey",
      teamName: "Moelbusters",
      opponent: "Shuds",
      score: "98.94 - 79.32",
      margin: 19.62,
      mvp: "Jordan Reed",
      mvpStats: "24.90 pts (proj 10.32)",
      championshipNumber: 1
    },
    {
      year: 2014,
      champion: "Shuds",
      teamName: "Flacco's ELITE Flock",
      opponent: "Sam",
      score: "91.14 - 75.48",
      margin: 15.66,
      mvp: "Marshawn Lynch",
      mvpStats: "23.30 pts (proj 11.52)",
      championshipNumber: 1
    },
    {
      year: 2013,
      champion: "Nipples",
      teamName: "Cobra Kai",
      opponent: "Lyons",
      score: "121.82 - 70.98",
      margin: 50.84,
      mvp: "Pierre Garcon",
      mvpStats: "20.40 pts (proj 10.27)",
      championshipNumber: 1
    },
    {
      year: 2012,
      champion: "Lyons",
      teamName: "RG-3PO",
      opponent: "Kevin",
      score: "100.7 - 97.27",
      margin: 3.43,
      mvp: "Dez Bryant",
      mvpStats: "26.93 pts (proj 8.93)",
      championshipNumber: 1
    }
  ], []);

  // Optimized helper functions with useCallback
  const getOrdinalSuffix = useCallback((num) => {
    const suffixes = ["th", "st", "nd", "rd"];
    const value = num % 100;
    return num + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
  }, []);

  // Updated function to handle different championship images
  const getChampionImageSrc = useCallback((championName, championshipNumber) => {
    const baseName = championName.toLowerCase().replace(/\s+/g, '');
    
    // For first championship, use the base image
    if (championshipNumber === 1) {
      return `/${baseName}champ.png`;
    } else {
      // For subsequent championships, try the numbered version first
      return `/${baseName}champ${championshipNumber}.png`;
    }
  }, []);

  const getHappyChampionImageSrc = useCallback((championName) => {
    return `/${championName.toLowerCase().replace(/\s+/g, '')}happy.png`;
  }, []);

  const getMvpImageSrc = useCallback((year) => {
    return `/${year}mvp.webp`;
  }, []);

  // Optimized modal handlers
  const handleOpenModal = useCallback((imageSrc, imageAlt) => {
    setFullscreenImage({ src: imageSrc, alt: imageAlt });
    document.body.style.overflow = 'hidden';
  }, []);

  const handleCloseModal = useCallback(() => {
    setFullscreenImage(null);
    document.body.style.overflow = 'unset';
  }, []);

  // Header animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHeaderAnimated(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Cleanup overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Optimized Modal Component with performance improvements
  const FullscreenModal = useMemo(() => {
    if (!fullscreenImage) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4"
        onClick={handleCloseModal}
      >
        <div className="relative max-w-7xl max-h-[95vh] w-full">
          <button
            onClick={handleCloseModal}
            className="absolute -top-16 right-0 text-white hover:text-gray-300 text-3xl font-bold bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center z-10"
            aria-label="Close modal"
          >
            ×
          </button>
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-2">
            <img
              src={fullscreenImage.src}
              alt={fullscreenImage.alt}
              className="w-full h-full object-contain rounded-xl shadow-2xl max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    );
  }, [fullscreenImage, handleCloseModal]);

  // Premium ChampionCard with proper out-of-bounds effect and image fallback
  const ChampionCard = React.memo(({ champion, index }) => {
    const isHovered = hoveredCard === champion.year;
    const is2025 = champion.year === 2025;
    const [championImageError, setChampionImageError] = useState(false);
    
    const handleMouseEnter = useCallback(() => {
      setHoveredCard(champion.year);
    }, [champion.year]);
    
    const handleMouseLeave = useCallback(() => {
      setHoveredCard(null);
    }, []);

    const handleChampionImageError = useCallback((e) => {
      if (!championImageError) {
        // If the numbered version fails, try the base version
        const baseName = champion.champion.toLowerCase().replace(/\s+/g, '');
        e.target.src = `/${baseName}champ.png`;
        setChampionImageError(true);
      } else {
        // If both fail, hide the image
        e.target.style.display = 'none';
        if (e.target.nextElementSibling) {
          e.target.nextElementSibling.style.display = 'flex';
        }
      }
    }, [championImageError, champion.champion]);
    
    return (
      <div className="w-full">
        <div
          className={`group relative overflow-hidden rounded-3xl transition-all duration-700 w-full ${
            isHovered ? 'scale-[1.02]' : 'scale-100'
          }`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            animationDelay: `${index * 150}ms`,
            filter: isHovered ? 'brightness(1.1) saturate(1.2)' : 'brightness(1)',
            boxShadow: isHovered 
              ? '0 40px 80px rgba(0,0,0,0.25), 0 0 120px rgba(255,215,0,0.3)' 
              : '0 20px 40px rgba(0,0,0,0.15)',
          }}
        >
          <div className="relative h-auto min-h-[600px] w-full overflow-visible">
            {/* Luxurious gradient background */}
            <div className={`absolute inset-0 ${
              is2025 
                ? 'bg-gradient-to-br from-amber-300 via-yellow-400 to-orange-500'
                : champion.isRecent 
                  ? 'bg-gradient-to-br from-amber-300 via-yellow-400 to-orange-500' 
                  : 'bg-gradient-to-br from-indigo-500 via-purple-600 to-violet-700'
            }`}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-white/20"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent"></div>
            </div>

            {/* Happy Champion Background Image - Subtle */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] opacity-8">
                <img 
                  src={getHappyChampionImageSrc(champion.champion)}
                  alt={`${champion.champion} Background`}
                  className="w-full h-full object-contain object-center filter blur-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                  loading="lazy"
                />
              </div>
            </div>

            {/* Premium Mobile Layout with proper out-of-bounds effect */}
            <div className="block lg:hidden relative p-6 sm:p-8 overflow-visible">
              <div className="space-y-6 overflow-visible">
                {/* Mobile Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <CustomCrown className="h-10 w-10" clickable={true} onOpenModal={handleOpenModal} />
                    <div>
                      <div className="text-3xl font-black text-white drop-shadow-lg tracking-tight">
                        {champion.year}
                      </div>
                      {is2025 ? (
                        <div className="bg-white/25 backdrop-blur-sm rounded-full px-3 py-1 border border-white/30 inline-block mt-1">
                          <span className="text-xs font-black text-white tracking-wider">LATEST</span>
                        </div>
                      ) : (
                        <div className="text-white/90 text-sm font-semibold tracking-wide uppercase">
                          Champion
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Screenshot */}
                  <button
                    className="w-16 h-12 rounded-xl overflow-hidden shadow-xl border-2 border-white/40 hover:scale-105 transition-transform duration-300 relative bg-black flex-shrink-0"
                    onClick={() => handleOpenModal(`/${champion.year}championship.png`, `${champion.year} Championship Game`)}
                    aria-label={`View ${champion.year} Championship Game`}
                  >
                    <img 
                      src={`/${champion.year}championship.png`}
                      alt={`${champion.year} Championship Game`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Eye className="h-3 w-3 text-white opacity-80" />
                    </div>
                  </button>
                </div>

                {/* Mobile Champion Section with True Out-of-Bounds Effect */}
                <div className="relative overflow-visible">
                  {/* Background card - this creates the "container" */}
                  <div className="bg-white/15 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-2xl overflow-visible">
                    <div className="flex flex-col items-center text-center space-y-4 overflow-visible">
                      {/* Champion image - simple centered */}
                      <div className="relative -mt-12 mb-2 overflow-visible flex justify-center">
                        <img 
                          src={getChampionImageSrc(champion.champion, champion.championshipNumber)}
                          alt={`${champion.champion} Champion Photo`}
                          className="w-44 h-44 sm:w-52 sm:h-52 object-contain drop-shadow-2xl relative z-20"
                          style={{
                            filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.4))'
                          }}
                          onError={handleChampionImageError}
                          loading="lazy"
                        />
                        <div className="w-44 h-44 sm:w-52 sm:h-52 rounded-3xl shadow-2xl bg-gradient-to-br from-yellow-400/80 to-amber-500/80 backdrop-blur-sm flex items-center justify-center relative z-20" style={{display: 'none'}}>
                          <CustomCrown className="h-20 w-20 sm:h-24 sm:w-24" clickable={true} onOpenModal={handleOpenModal} />
                        </div>
                        
                        {/* Enhanced glow effect */}
                        <div className="absolute inset-0 bg-gradient-radial from-white/50 via-white/25 to-transparent rounded-full blur-2xl scale-150 -z-10"></div>
                      </div>
                      
                      <div className="space-y-3 mt-4">
                        <h3 className="text-4xl sm:text-5xl font-black text-white drop-shadow-lg">
                          {champion.champion}
                        </h3>
                        <p className="text-white/90 text-xl sm:text-2xl font-medium italic">
                          {champion.teamName}
                        </p>
                        <div className="bg-white/25 backdrop-blur-sm rounded-full px-5 py-3 inline-block border border-white/30">
                          <span className="text-white font-bold text-base flex items-center">
                            <Trophy className="h-4 w-4 mr-2" />
                            {getOrdinalSuffix(champion.championshipNumber)} Championship
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile Game & MVP Grid - Compact */}
                <div className="grid grid-cols-1 gap-4">
                  {/* Championship Game */}
                  <div className="bg-white/15 backdrop-blur-xl rounded-2xl p-5 border border-white/20 shadow-2xl">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-white/30 rounded-xl p-2 shadow-lg">
                        <Swords className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-white font-bold text-base tracking-wide uppercase">
                        Championship Game
                      </h4>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white/80 font-medium text-sm">vs</span>
                        <div className="flex items-center space-x-2">
                          <OpponentAvatar 
                            opponentName={champion.opponent} 
                            size="small"
                            className="shadow-lg hover:shadow-red-500/50 transition-all duration-300"
                          />
                          <span className="text-white font-black text-base">{champion.opponent}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-white/80 font-medium text-sm">Score</span>
                        <span className="text-white font-black text-lg">{champion.score}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-white/80 font-medium text-sm">Margin</span>
                        <div className="bg-green-500/30 backdrop-blur-sm border border-green-400/40 text-green-100 font-bold px-3 py-1 rounded-full text-sm">
                          +{champion.margin.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MVP */}
                  <div className="bg-white/15 backdrop-blur-xl rounded-2xl p-5 border border-white/20 shadow-2xl">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-white/30 rounded-xl p-2 shadow-lg">
                        <Star className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-white font-bold text-base tracking-wide uppercase">
                        Championship MVP
                      </h4>
                    </div>

                    <div className="flex items-center space-x-3">
                      <button 
                        className="relative w-12 h-12 rounded-lg overflow-hidden shadow-lg border-2 border-white/30 flex-shrink-0 hover:scale-105 transition-transform duration-300 group"
                        onClick={() => handleOpenModal(getMvpImageSrc(champion.year), `${champion.mvp} - ${champion.year} Championship MVP`)}
                        aria-label={`View ${champion.mvp} MVP moment`}
                      >
                        <img 
                          src={getMvpImageSrc(champion.year)}
                          alt={`${champion.mvp} MVP Moment`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            if (e.target.src.includes('.webp')) {
                              e.target.src = `/${champion.year}mvp.png`;
                            } else {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }
                          }}
                          loading="lazy"
                        />
                        <div className="w-full h-full bg-gradient-to-br from-yellow-400/80 to-amber-500/80 backdrop-blur-sm flex items-center justify-center rounded-lg" style={{display: 'none'}}>
                          <Star className="h-4 w-4 text-white" />
                        </div>
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <Eye className="h-3 w-3 text-white" />
                        </div>
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-black text-lg mb-1 truncate">
                          {champion.mvp}
                        </p>
                        <p className="text-white/80 font-medium text-sm">
                          {champion.mvpStats}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Desktop Layout with proper out-of-bounds effect */}
            <div className="hidden lg:block relative p-8 lg:p-12 overflow-visible">
              <div className="grid grid-cols-12 gap-8 h-full min-h-[500px] overflow-visible">
                {/* Left: Champion Showcase with Out-of-Bounds */}
                <div className="col-span-4 flex flex-col justify-center items-center text-center overflow-visible">
                  {/* Champion image - simple centered */}
                  <div className="mb-8 relative -mt-16 overflow-visible flex justify-center">
                    <img 
                      src={getChampionImageSrc(champion.champion, champion.championshipNumber)}
                      alt={`${champion.champion} Champion Photo`}
                      className="w-80 h-80 object-contain drop-shadow-2xl relative z-20"
                      style={{
                        filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.4))'
                      }}
                      onError={handleChampionImageError}
                      loading="lazy"
                    />
                    <div className="w-80 h-80 rounded-3xl shadow-2xl bg-gradient-to-br from-yellow-400/80 to-amber-500/80 backdrop-blur-sm flex items-center justify-center relative z-20" style={{display: 'none'}}>
                      <CustomCrown className="h-32 w-32" clickable={true} onOpenModal={handleOpenModal} />
                    </div>
                    
                    {/* Enhanced glow effect */}
                    <div className="absolute inset-0 bg-gradient-radial from-white/50 via-white/25 to-transparent rounded-full blur-3xl scale-150 -z-10"></div>
                  </div>

                  <div>
                    <h3 className="text-5xl font-black text-white mb-3 drop-shadow-xl tracking-tight">
                      {champion.champion}
                    </h3>
                    <p className="text-white/95 text-2xl font-medium italic mb-4 tracking-wide">
                      {champion.teamName}
                    </p>
                    <div className="bg-white/25 backdrop-blur-sm rounded-full px-6 py-3 inline-block border border-white/30 mb-4">
                      <span className="text-white font-bold text-lg flex items-center">
                        <Trophy className="h-5 w-5 mr-3" />
                        {getOrdinalSuffix(champion.championshipNumber)} Championship
                      </span>
                    </div>
                    
                    {is2025 ? (
                      <div className="bg-white/30 backdrop-blur-sm rounded-full px-6 py-3 border border-white/40 inline-block">
                        <span className="text-sm font-black text-white tracking-wider">LATEST CHAMPION</span>
                      </div>
                    ) : champion.isRecent && (
                      <div className="bg-white/30 backdrop-blur-sm rounded-full px-6 py-3 border border-white/40 inline-block">
                        <span className="text-sm font-black text-white tracking-wider">RECENT</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Center: Year & Game Details */}
                <div className="col-span-4 flex flex-col justify-center space-y-8">
                  {/* Year Header */}
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-6 mb-6">
                      <div className="relative bg-white/25 backdrop-blur-xl rounded-3xl p-6 border border-white/40 shadow-2xl">
                        <CustomCrown className="h-20 w-20" clickable={true} onOpenModal={handleOpenModal} />
                        <div className="absolute inset-0 bg-gradient-radial from-yellow-400/30 via-amber-400/20 to-transparent rounded-3xl blur-lg scale-125 pointer-events-none"></div>
                      </div>
                    </div>
                    <div className="text-6xl font-black text-white drop-shadow-xl tracking-tight mb-2">
                      {champion.year}
                    </div>
                    <div className="text-white/90 text-xl font-semibold tracking-wide uppercase">
                      Champion
                    </div>
                  </div>

                  {/* Game Screenshot */}
                  <button
                    className="w-full h-40 rounded-2xl overflow-hidden shadow-2xl border-3 border-white/50 hover:scale-105 transition-all duration-300 relative bg-black group"
                    onClick={() => handleOpenModal(`/${champion.year}championship.png`, `${champion.year} Championship Game`)}
                    aria-label={`View ${champion.year} Championship Game`}
                  >
                    <img 
                      src={`/${champion.year}championship.png`}
                      alt={`${champion.year} Championship Game`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/25 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                        <Eye className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-white text-lg font-bold bg-black/70 px-4 py-3 rounded-xl backdrop-blur-sm text-center border border-white/20">
                        Click to View Championship Game
                      </div>
                    </div>
                  </button>

                  {/* Championship Details */}
                  <div className="bg-white/20 backdrop-blur-xl rounded-2xl p-6 border border-white/30 shadow-2xl">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="bg-white/30 rounded-xl p-4 shadow-lg">
                        <Swords className="h-8 w-8 text-white" />
                      </div>
                      <h4 className="text-white text-xl font-bold tracking-wide uppercase">
                        Championship Game
                      </h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-center space-x-6 mb-4">
                        <OpponentAvatar 
                          opponentName={champion.opponent} 
                          size="large"
                          className="shadow-2xl hover:shadow-red-500/50 transition-all duration-300"
                        />
                        <span className="text-white font-black text-3xl">vs</span>
                        <span className="text-white font-black text-2xl">{champion.opponent}</span>
                      </div>

                      <div className="text-center">
                        <div className="text-white font-black text-3xl mb-3">{champion.score}</div>
                        <div className="bg-green-500/30 backdrop-blur-sm border border-green-400/40 text-green-100 font-bold px-6 py-3 rounded-full inline-block">
                          Victory Margin: +{champion.margin.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: MVP Showcase */}
                <div className="col-span-4 flex flex-col justify-center space-y-6">
                  {/* MVP Image - Clickable */}
                  <button 
                    className="mb-6 h-56 rounded-2xl overflow-hidden shadow-2xl border-3 border-white/40 relative w-full hover:scale-105 transition-transform duration-300 group"
                    onClick={() => handleOpenModal(getMvpImageSrc(champion.year), `${champion.mvp} - ${champion.year} Championship MVP`)}
                    aria-label={`View ${champion.mvp} MVP moment`}
                  >
                    <img 
                      src={getMvpImageSrc(champion.year)}
                      alt={`${champion.mvp} MVP Moment`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.target.src.includes('.webp')) {
                          e.target.src = `/${champion.year}mvp.png`;
                        } else {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }
                      }}
                      loading="lazy"
                    />
                    <div className="w-full h-full bg-gradient-to-br from-yellow-400/80 to-amber-500/80 backdrop-blur-sm flex items-center justify-center" style={{display: 'none'}}>
                      <Star className="h-16 w-16 text-white" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="bg-black/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
                        <span className="text-white text-lg font-bold">Championship MVP</span>
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-white/25 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                        <Eye className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </button>

                  {/* MVP Details */}
                  <div className="bg-white/20 backdrop-blur-xl rounded-2xl p-6 border border-white/30 shadow-2xl text-center">
                    <div className="flex items-center justify-center space-x-4 mb-4">
                      <div className="bg-white/30 rounded-xl p-4 shadow-lg">
                        <Star className="h-8 w-8 text-white" />
                      </div>
                      <h4 className="text-white text-xl font-bold tracking-wide uppercase">
                        Championship MVP
                      </h4>
                    </div>
                    
                    <div>
                      <p className="text-white font-black text-3xl mb-3">
                        {champion.mvp}
                      </p>
                      <div className="bg-white/25 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/30">
                        <p className="text-white font-medium text-lg">
                          {champion.mvpStats}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-white/20 blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/15 blur-2xl animate-pulse"></div>
              <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-white/50 rounded-full animate-bounce"></div>
              <div className="absolute top-3/4 right-1/3 w-2 h-2 bg-yellow-200/60 rounded-full animate-bounce" style={{animationDelay: '700ms'}}></div>
            </div>

            {/* Premium Bottom Accent */}
            <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-white/60 via-white/80 to-white/60"></div>
          </div>
        </div>
      </div>
    );
  });

  // Optimized orbiting crown particles with better performance
  const OrbitingCrowns = useMemo(() => {
    return [...Array(8)].map((_, i) => {
      const randomDuration = 8 + Math.random() * 12;
      const randomDirection = Math.random() > 0.5 ? 'normal' : 'reverse';
      const randomDelay = Math.random() * 5;
      const orbitRadius = Math.min(120 + Math.random() * 40, 150);
      const angle = (i * 45) * Math.PI / 180;
      
      return (
        <div
          key={i}
          className="absolute w-12 h-12"
          style={{
            left: `calc(50% + ${Math.cos(angle) * orbitRadius}px - 24px)`,
            top: `calc(50% + ${Math.sin(angle) * orbitRadius}px - 24px)`,
            animation: `orbit-${i} ${randomDuration}s linear infinite ${randomDelay}s`,
            animationDirection: randomDirection
          }}
        >
          <CustomCrown className="h-8 w-8 opacity-70" />
        </div>
      );
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
      {/* Premium Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900">
          <div className="absolute top-20 left-1/4 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-20 right-1/4 w-24 h-24 bg-yellow-400/10 rounded-full blur-lg animate-bounce"></div>
          <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-blue-400/10 rounded-full blur-md animate-pulse" style={{animationDelay: '300ms'}}></div>
        </div>

        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto">
            <div className={`text-center mb-12 transform transition-all duration-1000 ${
              isHeaderAnimated ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div 
                    className={`absolute inset-0 transition-all duration-3000 delay-2000 ${
                      isHeaderAnimated ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-radial from-yellow-400/40 via-amber-400/25 to-transparent rounded-full blur-3xl animate-pulse w-64 h-64 -inset-16"></div>
                    <div className="absolute inset-0 border-4 border-yellow-400/30 rounded-full animate-spin w-48 h-48 -inset-8" style={{ animationDuration: '12s' }}></div>
                    <div className="absolute inset-0 bg-gradient-radial from-yellow-300/50 via-amber-400/30 to-transparent rounded-full blur-xl w-32 h-32 -inset-2"></div>
                  </div>
                  
                  <div 
                    className={`relative z-10 transition-all duration-2000 ease-out ${
                      isHeaderAnimated ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-75 translate-y-8'
                    }`}
                  >
                    <div className="relative">
                      <CustomCrown className="h-48 w-48 sm:h-52 sm:w-52 lg:h-56 lg:w-56" clickable={true} onOpenModal={handleOpenModal} />
                      
                      <div className="absolute inset-0 pointer-events-none">
                        {[...Array(12)].map((_, i) => {
                          const angle = (i * 30) * Math.PI / 180;
                          const radius = 120 + Math.random() * 40;
                          const x = 50 + Math.cos(angle) * (radius / 4);
                          const y = 50 + Math.sin(angle) * (radius / 4);
                          const delay = Math.random() * 3;
                          const duration = 1.5 + Math.random() * 2;
                          
                          return (
                            <div
                              key={i}
                              className="absolute w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-80"
                              style={{
                                left: `${x}%`,
                                top: `${y}%`,
                                animationDelay: `${delay}s`,
                                animationDuration: `${duration}s`,
                                boxShadow: '0 0 6px rgba(255, 215, 0, 0.8)'
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 leading-tight">
                <span className="bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-500 bg-clip-text text-transparent">
                  Hall of Champions
                </span>
              </h1>

              <p className="text-lg sm:text-xl lg:text-2xl text-purple-100 font-medium mb-8 max-w-3xl mx-auto leading-relaxed">
                Celebrating the legends, warriors, and champions of the 
                <span className="text-yellow-400 font-bold"> Beasts of Bellevue</span>
              </p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 via-blue-50/80 to-transparent"></div>
      </div>

      <div className="text-center py-8">
        <div className="inline-flex items-center space-x-2 text-gray-500 animate-bounce">
          <ChevronDown className="h-5 w-5" />
          <span className="text-sm font-medium">Scroll to explore champions</span>
          <ChevronDown className="h-5 w-5" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Beast Throne Section */}
        <div className="relative text-center mb-8 min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div 
              className="w-[700px] h-[700px] bg-gradient-radial from-yellow-400/50 via-amber-400/30 to-transparent rounded-full blur-3xl"
              style={{
                animation: 'throb 3s ease-in-out infinite'
              }}
            ></div>
            <div 
              className="absolute w-[600px] h-[600px] bg-gradient-radial from-orange-300/40 via-yellow-400/25 to-transparent rounded-full blur-2xl"
              style={{
                animation: 'throb 2.5s ease-in-out infinite 0.5s'
              }}
            ></div>
            <div 
              className="absolute w-[400px] h-[400px] bg-gradient-radial from-yellow-300/60 via-amber-400/40 to-transparent rounded-full blur-xl"
              style={{
                animation: 'throb 2s ease-in-out infinite 1s'
              }}
            ></div>
          </div>
          
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="w-[500px] h-[500px] border-4 border-yellow-400/25 rounded-full animate-spin" style={{ animationDuration: '25s' }}></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="w-[400px] h-[400px] border-2 border-amber-300/35 rounded-full animate-spin" style={{ animationDuration: '18s', animationDirection: 'reverse' }}></div>
          </div>

          <div 
            className="absolute inset-0 bg-center bg-no-repeat bg-contain opacity-100 z-20 beast-throne-bg"
            style={{
              backgroundImage: "url('/beastthrone2.png')",
              filter: 'drop-shadow(0 16px 64px rgba(255,215,0,1)) drop-shadow(0 0 128px rgba(255,215,0,0.8)) drop-shadow(0 0 200px rgba(255,165,0,0.6))',
              maskImage: 'radial-gradient(ellipse 85% 75% at center, black 25%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.1) 85%, transparent 95%)',
              WebkitMaskImage: 'radial-gradient(ellipse 85% 75% at center, black 25%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.1) 85%, transparent 95%)'
            }}
          ></div>

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 overflow-hidden">
            {OrbitingCrowns}
          </div>

          <div className="absolute inset-0 pointer-events-none z-25">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 bg-yellow-400 rounded-full animate-bounce opacity-60"
                style={{
                  left: `${10 + Math.random() * 80}%`,
                  top: `${10 + Math.random() * 80}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${2 + Math.random() * 3}s`
                }}
              />
            ))}
          </div>
        </div>

        {/* Title Section */}
        <div className="text-center mb-16">
          <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6 tracking-tight">
            Champions Through the Ages
          </h2>
          <p className="text-gray-600 text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
            Legends forged in battle, crowned in glory
          </p>
        </div>

        {/* Champions List */}
        <div className="space-y-8 lg:space-y-12">
          {championshipData.map((champion, index) => (
            <ChampionCard 
              key={champion.year}
              champion={champion} 
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {FullscreenModal}

      <style jsx>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
        
        .beast-throne-bg {
          background-size: 52.5% auto;
        }
        
        @media (min-width: 1024px) {
          .beast-throne-bg {
            background-size: 35% auto;
          }
        }
        
        @keyframes throb {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }
        
        @keyframes orbit-0 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes orbit-1 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes orbit-2 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes orbit-3 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes orbit-4 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes orbit-5 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes orbit-6 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes orbit-7 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default HallOfChampions;